/**
 * Mock interactive judge screen for quiz questions.
 * Replicates the real judge scoring UI but operates on local state.
 */

import { useState, useCallback, useEffect } from "react";

const WHOLE_SCORES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export interface MockRider {
  id: string;
  name: string;
  nickname?: string;
  attempts: number; // max attempts
  currentAttempt: number; // producer-controlled (0 = not started)
  scores: Record<number, number | null>; // attempt# → score (null = not scored)
}

export interface MockHeat {
  id: string;
  name: string;
  division?: string;
  isLive?: boolean;
}

interface MockJudgeScreenProps {
  heats: MockHeat[];
  activeHeatId: string;
  /** Riders per heat. If a flat array is passed, same riders for all heats. */
  riders?: MockRider[];
  ridersByHeat?: Record<string, MockRider[]>;
  /** Called whenever scores change — parent validates */
  onScoreChange?: (riderId: string, attempt: number, value: number, heatId: string) => void;
  /** Called when heat tab is clicked */
  onHeatSelect?: (heatId: string) => void;
}

export function MockJudgeScreen({ heats, activeHeatId, riders: flatRiders, ridersByHeat, onScoreChange, onHeatSelect }: MockJudgeScreenProps) {
  const getInitialRiders = (heatId: string) => ridersByHeat?.[heatId] ?? flatRiders ?? [];
  const [allRiders, setAllRiders] = useState<Record<string, MockRider[]>>(() => {
    const map: Record<string, MockRider[]> = {};
    for (const h of heats) {
      map[h.id] = getInitialRiders(h.id);
    }
    return map;
  });
  const [selectedHeat, setSelectedHeat] = useState(activeHeatId);

  // Sync external rider changes (e.g., producer advancing attempts)
  useEffect(() => {
    if (flatRiders) {
      setAllRiders((prev) => {
        const updated = { ...prev };
        for (const h of heats) {
          updated[h.id] = flatRiders.map((extR) => {
            const existing = prev[h.id]?.find((r) => r.id === extR.id);
            if (existing) {
              // Keep local scores but update currentAttempt from external
              return { ...existing, currentAttempt: extR.currentAttempt, attempts: extR.attempts };
            }
            return extR;
          });
        }
        return updated;
      });
    }
  }, [flatRiders]);

  const riders = allRiders[selectedHeat] ?? [];
  const [expanded, setExpanded] = useState<Record<string, number | null>>({});
  const [scoringMode, setScoringMode] = useState<Record<string, boolean>>({});
  const [viewAttempt, setViewAttempt] = useState<Record<string, number>>(() => {
    const a: Record<string, number> = {};
    for (const r of riders) {
      let start = 1;
      for (let i = 1; i <= r.currentAttempt; i++) {
        if (r.scores[i] == null) { start = i; break; }
        start = i + 1;
      }
      a[r.id] = Math.min(start, Math.max(r.currentAttempt, 1));
    }
    return a;
  });

  const setScore = useCallback((riderId: string, attempt: number, value: number) => {
    setAllRiders((prev) => ({
      ...prev,
      [selectedHeat]: (prev[selectedHeat] ?? []).map((r) =>
        r.id === riderId ? { ...r, scores: { ...r.scores, [attempt]: value } } : r
      ),
    }));
    setExpanded((prev) => ({ ...prev, [riderId]: null }));
    setScoringMode((prev) => ({ ...prev, [riderId]: false }));
    onScoreChange?.(riderId, attempt, value, selectedHeat);

    // Auto-advance to next attempt if available
    const rider = riders.find((r) => r.id === riderId);
    if (rider && attempt < rider.currentAttempt) {
      setViewAttempt((prev) => ({ ...prev, [riderId]: attempt + 1 }));
    }
  }, [onScoreChange, riders, selectedHeat]);

  const handleHeatSelect = useCallback((heatId: string) => {
    setSelectedHeat(heatId);
    setExpanded({});
    setScoringMode({});
    // Reset view attempts for the new heat's riders
    const newRiders = allRiders[heatId] ?? [];
    const a: Record<string, number> = {};
    for (const r of newRiders) {
      let start = 1;
      for (let i = 1; i <= r.currentAttempt; i++) {
        if (r.scores[i] == null) { start = i; break; }
        start = i + 1;
      }
      a[r.id] = Math.min(start, Math.max(r.currentAttempt, 1));
    }
    setViewAttempt(a);
    onHeatSelect?.(heatId);
  }, [onHeatSelect, allRiders]);

  // Group heats by division
  const divisionMap = new Map<string | undefined, MockHeat[]>();
  for (const h of heats) {
    const key = h.division;
    if (!divisionMap.has(key)) divisionMap.set(key, []);
    divisionMap.get(key)!.push(h);
  }

  return (
    <div>
      {/* Heat tabs */}
      <div style={{ marginBottom: 16 }}>
        {Array.from(divisionMap.entries()).map(([division, divHeats]) => (
          <div key={division ?? "__none"} style={{ marginBottom: 8 }}>
            {division && (
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, fontWeight: 600, color: "var(--c-text-dim)" }}>
                {division}
              </div>
            )}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {divHeats.map((h) => (
                <button
                  key={h.id}
                  className={`btn ${selectedHeat === h.id ? "btn-primary" : "btn-secondary"} btn-sm`}
                  onClick={() => handleHeatSelect(h.id)}
                  style={{ position: "relative" }}
                >
                  {h.name}
                  {h.isLive && (
                    <span style={{
                      position: "absolute", top: -3, right: -3,
                      width: 8, height: 8, borderRadius: "50%",
                      background: "#ef4444", boxShadow: "0 0 4px #ef4444",
                    }} />
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Rider grid */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {/* Header */}
        <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 8, padding: "0 8px" }}>
          <span className="field-label">Rider</span>
          <span className="field-label">Score</span>
        </div>

        {riders.map((r) => {
          const maxAttempts = r.attempts;
          const currentAttempt = r.currentAttempt;
          const viewing = viewAttempt[r.id] ?? 1;
          const currentScore = r.scores[viewing] ?? null;
          const inScoringMode = scoringMode[r.id] ?? (maxAttempts <= 1);
          const expandedNum = expanded[r.id] ?? null;
          const waitingForAttempt = (() => {
            for (let a = 1; a <= currentAttempt; a++) {
              if (r.scores[a] == null) return false;
            }
            return currentAttempt > 0;
          })();

          return (
            <div key={r.id} style={{
              display: "grid", gridTemplateColumns: "160px 1fr", gap: 8,
              padding: "6px 8px", alignItems: "center",
              background: currentScore != null ? "var(--c-surface)" : "transparent",
              border: "1px solid var(--c-border)",
            }}>
              {/* Name */}
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>{r.name}</div>
                {r.nickname && <div style={{ fontSize: 10, color: "var(--c-text-dim)" }}>{r.nickname}</div>}
              </div>

              {/* Score area */}
              <div style={{ display: "flex", gap: 2, flexWrap: "nowrap", overflow: "hidden" }}>
                {!inScoringMode ? (
                  /* Attempt boxes */
                  <>
                    {Array.from({ length: maxAttempts }, (_, i) => {
                      const attemptNum = i + 1;
                      const score = r.scores[attemptNum] ?? null;
                      const hasScore = score !== null;
                      const isLive = attemptNum === currentAttempt;
                      const isAvailable = attemptNum <= currentAttempt;
                      const isFuture = attemptNum > currentAttempt;
                      const needsScore = isAvailable && !hasScore;

                      return (
                        <button key={attemptNum}
                          onClick={() => {
                            if (isFuture) return;
                            setViewAttempt((prev) => ({ ...prev, [r.id]: attemptNum }));
                            setExpanded((prev) => ({ ...prev, [r.id]: null }));
                            setScoringMode((prev) => ({ ...prev, [r.id]: true }));
                          }}
                          style={{
                            flex: "1 1 0", minWidth: 0, height: 32,
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                            fontSize: hasScore ? 11 : 9, fontWeight: hasScore ? 700 : 500,
                            fontFamily: "var(--font-mono)",
                            background: hasScore ? "var(--c-surface-2)" : needsScore ? "rgba(234,179,8,0.1)" : "transparent",
                            color: hasScore ? "var(--c-text)" : needsScore ? "#eab308" : isFuture ? "var(--c-text-dim)" : "var(--c-text-muted)",
                            border: `1px solid ${needsScore ? "#eab308" : isLive ? "#22c55e" : "var(--c-border)"}`,
                            boxShadow: isLive && !needsScore ? "0 0 0 1px #22c55e" : "none",
                            cursor: isFuture ? "default" : "pointer",
                            opacity: isFuture ? 0.3 : 1,
                            padding: 0,
                          }}
                        >
                          {hasScore ? score.toFixed(1) : `#${attemptNum}`}
                          {needsScore && <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#eab308" }} />}
                        </button>
                      );
                    })}
                    {waitingForAttempt && maxAttempts > 1 && (
                      <div style={{
                        flex: "1 1 0", minWidth: 0, height: 32,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 9, color: "var(--c-text-dim)", fontStyle: "italic",
                        background: "var(--c-surface)", border: "1px dashed var(--c-border)",
                      }}>
                        Waiting
                      </div>
                    )}
                  </>
                ) : expandedNum != null ? (
                  /* Decimal sub-grid */
                  <>
                    <button onClick={() => setExpanded((prev) => ({ ...prev, [r.id]: null }))}
                      style={{ flex: "0 0 24px", height: 32, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--c-surface-2)", color: "var(--c-text-muted)", border: "1px solid var(--c-border)", cursor: "pointer", fontSize: 12, padding: 0 }}>
                      &larr;
                    </button>
                    {Array.from({ length: 10 }, (_, i) => {
                      const val = Math.round((expandedNum + i * 0.1) * 10) / 10;
                      const isSelected = currentScore === val;
                      return (
                        <button key={val} onClick={() => setScore(r.id, viewing, val)}
                          style={{
                            flex: "1 1 0", minWidth: 0, height: 32,
                            fontSize: 10, fontWeight: isSelected ? 700 : 500, fontFamily: "var(--font-mono)",
                            background: isSelected ? "var(--c-accent)" : "var(--c-surface-2)",
                            color: isSelected ? "#fff" : "var(--c-text-muted)",
                            border: "1px solid transparent", cursor: "pointer", padding: 0,
                          }}>
                          {val.toFixed(1)}
                        </button>
                      );
                    })}
                  </>
                ) : (
                  /* Whole number picker */
                  <>
                    {maxAttempts > 1 && (
                      <button onClick={() => { setScoringMode((prev) => ({ ...prev, [r.id]: false })); setExpanded((prev) => ({ ...prev, [r.id]: null })); }}
                        style={{ flex: "0 0 24px", height: 32, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--c-surface-2)", color: "var(--c-text-muted)", border: "1px solid var(--c-border)", cursor: "pointer", fontSize: 12, padding: 0 }}>
                        &larr;
                      </button>
                    )}
                    {WHOLE_SCORES.map((v) => {
                      const isSelected = currentScore != null && Math.floor(currentScore) === v;
                      return (
                        <button key={v}
                          onClick={() => {
                            if (v === 10) { setScore(r.id, viewing, 10.0); }
                            else { setExpanded((prev) => ({ ...prev, [r.id]: v })); }
                          }}
                          style={{
                            flex: "1 1 0", minWidth: 0, height: 32,
                            fontSize: 11, fontWeight: isSelected ? 700 : 500, fontFamily: "var(--font-mono)",
                            background: isSelected ? "var(--c-accent)" : "var(--c-surface-2)",
                            color: isSelected ? "#fff" : "var(--c-text-muted)",
                            border: "1px solid transparent", cursor: "pointer", padding: 0,
                          }}>
                          {v}
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
